import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

export interface GalleryCarouselImage {
  id: string;
  url: string;
}

interface GalleryCarouselProps {
  images: GalleryCarouselImage[];
}

/**
 * Read-only image carousel used on public shop/barber profiles to display
 * uploaded gallery images, with a thumbnail strip and a full-size lightbox.
 */
const GalleryCarousel: React.FC<GalleryCarouselProps> = ({ images }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const total = images.length;

  const prev = useCallback(
    () => setActiveIndex((i) => (i - 1 + total) % total),
    [total]
  );
  const next = useCallback(
    () => setActiveIndex((i) => (i + 1) % total),
    [total]
  );

  useEffect(() => {
    if (total > 0 && activeIndex >= total) setActiveIndex(0);
  }, [total, activeIndex]);

  if (total === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: 'rgba(255,255,255,0.4)',
          gap: 1,
          bgcolor: '#1b1c22',
          borderRadius: 2,
        }}
      >
        <PhotoLibraryIcon sx={{ fontSize: 48 }} />
        <Typography variant="body2">No gallery images yet.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 240, sm: 360 },
          bgcolor: '#000',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <ButtonBase
          onClick={() => setLightboxOpen(true)}
          aria-label={`View gallery image ${
            activeIndex + 1
          } of ${total} full size`}
          sx={{
            width: '100%',
            height: '100%',
            display: 'block',
          }}
        >
          <Box
            component="img"
            src={images[activeIndex]?.url}
            alt={`Gallery image ${activeIndex + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              cursor: 'zoom-in',
              display: 'block',
            }}
          />
        </ButtonBase>

        {total > 1 && (
          <>
            <IconButton
              aria-label="Previous image"
              onClick={prev}
              size="small"
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.4)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="Next image"
              onClick={next}
              size="small"
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.4)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </>
        )}

        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 10,
            right: 12,
            color: '#fff',
            fontWeight: 'bold',
          }}
        >
          {activeIndex + 1} / {total}
        </Typography>
      </Box>

      {total > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            mt: 1.5,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: 4 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.2)',
              borderRadius: 2,
            },
          }}
        >
          {images.map((img, i) => (
            <ButtonBase
              key={img.id}
              onClick={() => setActiveIndex(i)}
              aria-label={`View image ${i + 1}`}
              aria-current={i === activeIndex}
              sx={{
                flexShrink: 0,
                borderRadius: 1,
              }}
            >
              <Box
                component="img"
                src={img.url}
                alt={`Thumbnail ${i + 1}`}
                sx={{
                  width: 72,
                  height: 56,
                  objectFit: 'cover',
                  borderRadius: 1,
                  cursor: 'pointer',
                  outline:
                    i === activeIndex
                      ? '2px solid #dd8709'
                      : '2px solid transparent',
                  opacity: i === activeIndex ? 1 : 0.6,
                  transition: 'all 0.2s',
                }}
              />
            </ButtonBase>
          ))}
        </Box>
      )}

      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: '#000' } }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {images[activeIndex] && (
            <Box
              component="img"
              src={images[activeIndex].url}
              alt="Full size"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                objectFit: 'contain',
                maxHeight: '80vh',
              }}
            />
          )}
          {total > 1 && (
            <>
              <IconButton
                onClick={prev}
                sx={{
                  position: 'absolute',
                  left: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton
                onClick={next}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#fff',
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default GalleryCarousel;
